import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import type {
    ITableLikeTreeItem,
    ITableLikeTreeItemNotification,
    ITableLikeTreeCheckConfirmation,
    ITableLikeTreeMenuOperationContext,
    ITableLikeTreeFilterItem,
} from 'components/TableLikeTree/context';
import type { ITableLikeTreeFilterConfig } from 'components/TableLikeTree/TableLikeTree';
import { TableLikeTree } from 'components/TableLikeTree/TableLikeTree';
import { RequestStatus } from 'features/request/requestSlice';
import type { BimWorkOrderObjectSelectionData } from 'gql/graphql';
import { BimContractObjectStatusEnum, WorkOrderChangeStatusEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { useProjectData } from 'hooks/useProjectData';
import { ProjectPageLayout } from 'pages/Project/Common/Layouts/ProjectPageLayout';
import type { TConstructionTypesIndexes } from 'pages/Project/Common/types';
import { Button, Layout, Loader, cnMixSpace, Switch, Badge, IconAlert } from 'uikit';
import { isNumber } from 'utils';
import { ExportCSVReport } from 'utils/createCSVReportFile';
import { descriptionNumberAndDate } from 'utils/descriptionNumberAndDate';

import cn from 'classnames';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { BimWorkOrderObjectEdit } from '../../WorkOrder/BimWorkOrderObjectEdit';
import {
    confirmOperationMessages,
    groupByParentId,
    sortByParentBaseOrder,
} from '../../WorkOrder/BimWorkOrderStructure';
import styles from '../../WorkOrder/BimWorkOrderStructure/BimWorkOrderStructure.module.css';
import { useBimWorkOrderContractSeasonList } from '../../WorkOrder/BimWorkOrderStructure/hooks';
import { BimWorkOrderSubObjectEdit } from '../../WorkOrder/BimWorkOrderSubObjectEdit';
import { useGetBimWorkOrderDataFromProject } from '../../WorkOrder/hooks';
import { BimWorkOrderStructureOperation } from '../../WorkOrder/types';
import { useBimWorkOrderChange } from '../BimWorkOrderChange/hooks';
import { useBimRemoveWorkOrderChangeObject } from '../BimWorkOrderChange/hooks/useBimRemoveWorkOrderChangeObject';
import { useContractChangeConstructionTypes } from '../hooks';

import {
    useBimExcludeContractObjectToWorkOrderChange,
    useBimIncludeContractObjectToWorkOrderChange,
    useBimWorkOrderChangeObjectSelectionList,
    useBimWorkOrderChangeSeasonRange,
    useIncludedChangeCountByConstructionType,
    useBimUpdateWorkOrderChangeSeasons,
    useBimWorkOrderChangeLastSeasonSelectionList,
} from './hooks';

export const BimWorkOrderChangeStructure = () => {
    const navigate = useNavigate();

    const tableRef = useRef<HTMLDivElement>(null);

    const { id: projectId, methodology, orderChangeId } = useParams();

    const workOrderChangeId = useMemo(() => Number(orderChangeId), [orderChangeId]);

    const { projectData, projectDataStatus } = useProjectData(projectId);
    const { isFrameContract, workOrderStatus } = useGetBimWorkOrderDataFromProject({});
    const [constructionTypes] = useContractChangeConstructionTypes(workOrderChangeId);
    const [selectedSeasonsIds] = useState<number[]>([]);
    const [checkedIds, setCheckedIds] = useState<number[]>([]);
    const [showOnlyChecked, setShowOnlyChecked] = useState(false);
    const [workOrderStructureStyle, setWorkOrderStructureStyle] = useState<React.CSSProperties>();
    const [constructionTypeId, setConstructionTypeId] = useState<number>();
    const [objectToEdit, setObjectToEdit] = useState<BimWorkOrderObjectSelectionData>();
    const [operationToConfirm, setOperationToConfirm] = useState<BimWorkOrderStructureOperation>();
    const [isOpenCreateOrEditObject, setIsOpenCreateOrEditObject] = useState(false);
    const [isOpenCreateOrEditSubObject, setIsOpenCreateOrEditSubObject] = useState(false);
    const [objectToAppendTo, setObjectToAppendTo] = useState<BimWorkOrderObjectSelectionData>();
    const [isErrorSubmitted, setIsErrorSubmitted] = useState<boolean>(false);

    const { workOrderChangeData } = useBimWorkOrderChange(workOrderChangeId);

    const { textConfirmation, textHeader, confirmButtonText, cancelButtonText } = useMemo(
        () => confirmOperationMessages[operationToConfirm as BimWorkOrderStructureOperation] || {},
        [operationToConfirm],
    );

    const workOrderMessageTitle = useMemo(
        () => `Изм.${descriptionNumberAndDate(workOrderChangeData?.internalNumber, workOrderChangeData?.signingDate)}`,
        [workOrderChangeData?.internalNumber, workOrderChangeData?.signingDate],
    );

    const isWorkOrderChangeStatusOnFormation = workOrderChangeData?.status === WorkOrderChangeStatusEnum.OnFormation;

    const title = `Редактирование структуры ${workOrderMessageTitle}`;
    const saveButtonTitle = 'Завершить редактирование';

    const contractId = projectData?.contractId;

    const { includedCount, setIncludedCount } = useIncludedChangeCountByConstructionType({
        workOrderChangeId,
    });

    const {
        bimWorkOrderSeasonSelectionReset,
        bimWorkOrderContractSeasonListData,
        bimWorkOrderContractSeasonListStatus,
    } = useBimWorkOrderContractSeasonList({ contractId });

    const { bimWorkOrderSeasonRangeData, bimWorkOrderChangeSeasonRangeUpdate } = useBimWorkOrderChangeSeasonRange({
        workOrderChangeId,
    });

    const { updateSeasonsHandler, updateSeasonsValidationMessages, updateWorkOrderSeasonsStatus } =
        useBimUpdateWorkOrderChangeSeasons({
            workOrderChangeId,
            updateSeasonRange: bimWorkOrderChangeSeasonRangeUpdate,
        });

    const {
        bimWorkOrderChangeObjectSelectionListReset,
        bimWorkOrderChangeObjectSelectionListData,
        bimWorkOrderChangeObjectSelectionListStatus,
        bimWorkOrderChangeObjectSelectionListMakeRequest,
    } = useBimWorkOrderChangeObjectSelectionList();

    const updateIncludedCount = useCallback(
        (includeLen: number, isReplace = false) => {
            const key = String(constructionTypeId);

            setIncludedCount(
                (prevIncludedCount) =>
                    ({
                        ...prevIncludedCount,

                        [key]: isReplace
                            ? includeLen
                            : (prevIncludedCount?.[key as TConstructionTypesIndexes] || 0) + includeLen,
                    }) as any,
            );
        },
        [constructionTypeId, setIncludedCount],
    );

    const onDeleteObjectSuccess = useCallback(() => {
        setObjectToEdit(undefined);
        setOperationToConfirm(undefined);

        if (workOrderChangeId) {
            bimWorkOrderChangeObjectSelectionListMakeRequest(workOrderChangeId, constructionTypeId);
        }
    }, [bimWorkOrderChangeObjectSelectionListMakeRequest, constructionTypeId, workOrderChangeId]);

    const {
        bimRemoveWorkOrderChangeObjectData,
        bimRemoveWorkOrderChangeObjectStatus,
        bimRemoveWorkOrderChangeObjectError,
        bimRemoveWorkOrderObjectMakeChangeRequest,
    } = useBimRemoveWorkOrderChangeObject(operationToConfirm, onDeleteObjectSuccess);

    // + include

    const onIncludeSuccess = useCallback(
        (items?: number[] | null) => {
            const includeLen = items?.length || 0;
            const checksToInclude = items?.filter((id) => !checkedIds.includes(id)) || [];

            updateIncludedCount(includeLen);
            setCheckedIds([...checkedIds, ...checksToInclude]);
        },
        [checkedIds, updateIncludedCount],
    );

    const onIncludeExcludeError = useCallback(() => {
        setCheckedIds([...checkedIds]);
    }, [checkedIds]);

    const { bimIncludeContractObjectToWorkOrderStatus, bimIncludeContractObjectToWorkOrderMakeRequest } =
        useBimIncludeContractObjectToWorkOrderChange({
            workOrderChangeId,
            onSuccess: onIncludeSuccess,
            onError: onIncludeExcludeError,
        });

    // - include

    // + exclude:

    const onExcludeSuccess = useCallback(
        (items?: number[] | null) => {
            const excludeLen = items?.length || 0;
            const checksToExclude = checkedIds.filter((id) => !items?.includes(id));

            updateIncludedCount(-excludeLen);
            setCheckedIds(checksToExclude);
        },
        [checkedIds, updateIncludedCount],
    );

    const { bimExcludeContractObjectFromWorkOrderStatus, bimExcludeContractObjectFromWorkOrderMakeRequest } =
        useBimExcludeContractObjectToWorkOrderChange({
            workOrderChangeId,
            onSuccess: onExcludeSuccess,
            onError: onIncludeExcludeError,
        });

    // - exclude:

    const informers: ITableLikeTreeItemNotification[] = useMemo(
        () =>
            bimWorkOrderChangeObjectSelectionListData?.bimWorkOrderChangeObjectSelectionList?.items?.reduce(
                (acc: ITableLikeTreeItemNotification[], item) => {
                    const excludeItem = updateSeasonsValidationMessages.find(
                        (i) => i?.metadata?.contractObjectId === item.id && checkedIds.includes(item.id),
                    );

                    const items = [
                        ...(excludeItem
                            ? [
                                  {
                                      message: `${excludeItem.message}${
                                          excludeItem.message && !excludeItem.message.endsWith('.') ? '.' : ''
                                      } Скорректируйте распределение и повторно удалите сезон.`,
                                      icon: <IconAlert size="xs" view="alert" />,
                                  },
                              ]
                            : []),
                        ...(item.status === BimContractObjectStatusEnum.NewRecord
                            ? [{ icon: <Badge minified size="s" status="success" /> }]
                            : []),
                        ...(item.hasPrices
                            ? [
                                  {
                                      message: item.parentId
                                          ? 'В состав подобъекта загружены сметы'
                                          : 'В состав объекта загружены сметы',
                                      icon: <Badge size="s" status="system" label="C" />,
                                  },
                              ]
                            : []),
                    ];

                    if (items.length) {
                        acc.push({
                            items,
                            treeItemId: item.id,
                        });
                    }

                    return acc;
                },
                [],
            ) || [],
        [
            bimWorkOrderChangeObjectSelectionListData?.bimWorkOrderChangeObjectSelectionList?.items,
            checkedIds,
            updateSeasonsValidationMessages,
        ],
    );

    const checkConfirmations: ITableLikeTreeCheckConfirmation[] = useMemo(
        () =>
            bimWorkOrderChangeObjectSelectionListData?.bimWorkOrderChangeObjectSelectionList?.items
                ?.filter((i) => i.hasPrices)
                ?.map((i) => ({
                    treeItemId: i.id,
                    onUncheck: {
                        message: `В составе данного ${
                            i.parentId ? 'подобъекта' : 'объекта'
                        } загружены сметы. Вы уверены, что хотите исключить ${
                            i.parentId ? 'подобъект' : 'объект'
                        } из ${workOrderMessageTitle}?`,
                        confirmLabel: 'Исключить',
                    },
                })) || [],
        [
            bimWorkOrderChangeObjectSelectionListData?.bimWorkOrderChangeObjectSelectionList?.items,
            workOrderMessageTitle,
        ],
    );

    const dragOperationDisabledItemsIds = useMemo(
        () =>
            bimWorkOrderChangeObjectSelectionListData?.bimWorkOrderChangeObjectSelectionList?.items
                ?.filter((i) => !i.workOrderObjectId)
                .map((i) => i.id),
        [bimWorkOrderChangeObjectSelectionListData?.bimWorkOrderChangeObjectSelectionList?.items],
    );

    const areCheckboxesDisabled = useMemo(
        () =>
            !isFrameContract ||
            bimIncludeContractObjectToWorkOrderStatus === RequestStatus.FETCHING ||
            bimExcludeContractObjectFromWorkOrderStatus === RequestStatus.FETCHING ||
            !isWorkOrderChangeStatusOnFormation,
        [
            bimExcludeContractObjectFromWorkOrderStatus,
            bimIncludeContractObjectToWorkOrderStatus,
            isFrameContract,
            isWorkOrderChangeStatusOnFormation,
        ],
    );

    const tableTreeItems: ITableLikeTreeItem[] = useMemo(() => {
        if (bimWorkOrderChangeObjectSelectionListData?.bimWorkOrderChangeObjectSelectionList?.items?.length) {
            const groupedByParent = groupByParentId(
                bimWorkOrderChangeObjectSelectionListData?.bimWorkOrderChangeObjectSelectionList
                    ?.items as BimWorkOrderObjectSelectionData[],
            );
            const mapper = (o: BimWorkOrderObjectSelectionData): ITableLikeTreeItem => ({
                id: o.id,
                title: o.name,
                children: sortByParentBaseOrder(groupedByParent[o.id] || [])?.map(mapper),
            });

            return sortByParentBaseOrder(groupedByParent.root || []).map(mapper);
        }

        return [];
    }, [bimWorkOrderChangeObjectSelectionListData?.bimWorkOrderChangeObjectSelectionList?.items]);

    const isLoadingPage = useMemo(
        () =>
            projectDataStatus === RequestStatus.FETCHING ||
            bimWorkOrderContractSeasonListStatus === RequestStatus.FETCHING ||
            (bimWorkOrderChangeObjectSelectionListStatus === RequestStatus.FETCHING && !constructionTypeId),
        [
            bimWorkOrderChangeObjectSelectionListStatus,
            bimWorkOrderContractSeasonListStatus,
            constructionTypeId,
            projectDataStatus,
        ],
    );

    const breadCrumbsItems = useMemo(
        () => [
            {
                label: projectData?.name,
                href: `/projects/${methodology}/${projectData?.uniqueId}`,
            },
            {
                label: 'Договор',
                href: `/projects/${methodology}/${projectData?.uniqueId}/contract`,
            },
        ],
        [methodology, projectData?.uniqueId, projectData?.name],
    );

    const { seasonSelectionList: availableLastSeasons } =
        useBimWorkOrderChangeLastSeasonSelectionList(workOrderChangeId);

    const topFilterConfig: ITableLikeTreeFilterConfig = useMemo(
        () => ({
            filterItems: (bimWorkOrderContractSeasonListData ?? []).map((s) => {
                const isFirstActive = bimWorkOrderSeasonRangeData?.firstSeason?.contractSeason?.id === s?.id;
                const isLastActive = bimWorkOrderSeasonRangeData?.lastSeason?.contractSeason?.id === s?.id;
                const isSeasonAvailable = availableLastSeasons.some((as) => as.id === s?.id);

                return { id: s?.id, title: s?.year, isFirstActive, isLastActive, disabled: !isSeasonAvailable };
            }),
            title: 'Выбор сезонов',
            isFictiveFilter: true,
            onFilterItemCheckChange: (seasonValues) => {
                if (seasonValues) {
                    updateSeasonsHandler(
                        seasonValues as {
                            lastSeason: ITableLikeTreeFilterItem | undefined;
                        },
                    );
                }
            },
            disabled:
                (!isFrameContract &&
                    [WorkOrderStatusEnum.OnFormation, WorkOrderStatusEnum.InProgress].includes(workOrderStatus)) ||
                (isFrameContract && workOrderStatus === WorkOrderStatusEnum.InProgress) ||
                updateWorkOrderSeasonsStatus === RequestStatus.FETCHING ||
                !isWorkOrderChangeStatusOnFormation ||
                (bimWorkOrderContractSeasonListData?.length || 0) <= 1,
            noFirstListSelectionInSecond: (bimWorkOrderContractSeasonListData?.length || 0) > 1,
        }),
        [
            bimWorkOrderContractSeasonListData,
            isFrameContract,
            workOrderStatus,
            updateWorkOrderSeasonsStatus,
            isWorkOrderChangeStatusOnFormation,
            bimWorkOrderSeasonRangeData?.firstSeason?.contractSeason?.id,
            bimWorkOrderSeasonRangeData?.lastSeason?.contractSeason?.id,
            availableLastSeasons,
            updateSeasonsHandler,
        ],
    );

    const sortedConstructionTypesTabsItems = useMemo(
        () =>
            constructionTypes
                ?.map((ct) => ({ id: ct.id, title: ct.name }))
                .sort((a, b) => a.title.localeCompare(b.title)),
        [constructionTypes],
    );

    const leftFilterConfig: ITableLikeTreeFilterConfig = useMemo(
        () => ({
            filterItems: sortedConstructionTypesTabsItems,
            isFictiveFilter: true,
            disabled: true,
            title: 'Виды строительства',
        }),
        [sortedConstructionTypesTabsItems],
    );

    const disabledCheckBoxesItemsIds = useMemo(
        () =>
            workOrderStatus === WorkOrderStatusEnum.InProgress
                ? bimWorkOrderChangeObjectSelectionListData?.bimWorkOrderChangeObjectSelectionList?.items
                      ?.filter(
                          (i) =>
                              (i.status === BimContractObjectStatusEnum.ContractCondition &&
                                  i.isIncluded &&
                                  !i.isActKC2Object) ||
                              i.hasActsKC2,
                      )
                      ?.map((i) => i.id)
                : [],
        [bimWorkOrderChangeObjectSelectionListData?.bimWorkOrderChangeObjectSelectionList?.items, workOrderStatus],
    );

    const actionFn = useCallback(() => {
        if (operationToConfirm === BimWorkOrderStructureOperation.CreateObject) {
            setIsOpenCreateOrEditObject(true);
        } else if (objectToEdit) {
            const obj = bimWorkOrderChangeObjectSelectionListData?.bimWorkOrderChangeObjectSelectionList?.items?.find(
                (o) => o.id === objectToEdit.id,
            );

            if (obj && workOrderChangeId) {
                bimRemoveWorkOrderObjectMakeChangeRequest(workOrderChangeId, obj.id);
                setIsErrorSubmitted(true);
            }
        }
    }, [
        bimRemoveWorkOrderObjectMakeChangeRequest,
        bimWorkOrderChangeObjectSelectionListData?.bimWorkOrderChangeObjectSelectionList?.items,
        objectToEdit,
        operationToConfirm,
        workOrderChangeId,
    ]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn,
        });

    const onDeleteObject = useCallback(
        (itemId?: number, context?: ITableLikeTreeMenuOperationContext) => {
            const item = context?.item;

            setOperationToConfirm(
                item?.parent
                    ? BimWorkOrderStructureOperation.DeleteSubObject
                    : BimWorkOrderStructureOperation.DeleteObject,
            );

            const obj = bimWorkOrderChangeObjectSelectionListData?.bimWorkOrderChangeObjectSelectionList?.items?.find(
                (o) => o.id === item?.id,
            );

            setObjectToEdit(obj as BimWorkOrderObjectSelectionData);

            handleOpenConfirmationDialog();
        },
        [
            bimWorkOrderChangeObjectSelectionListData?.bimWorkOrderChangeObjectSelectionList?.items,
            handleOpenConfirmationDialog,
        ],
    );

    const onOpenCreateSubObject = useCallback(
        (itemId?: number, context?: ITableLikeTreeMenuOperationContext) => {
            const item = context?.item;

            const obj = bimWorkOrderChangeObjectSelectionListData?.bimWorkOrderChangeObjectSelectionList?.items?.find(
                (o) => o.id === item?.id,
            );

            setObjectToAppendTo(obj as BimWorkOrderObjectSelectionData);

            setIsOpenCreateOrEditSubObject(true);
        },
        [bimWorkOrderChangeObjectSelectionListData?.bimWorkOrderChangeObjectSelectionList?.items],
    );

    const onOpenEditObject = useCallback(
        (itemId?: number, context?: ITableLikeTreeMenuOperationContext) => {
            const item = context?.item;

            const obj = bimWorkOrderChangeObjectSelectionListData?.bimWorkOrderChangeObjectSelectionList?.items?.find(
                (o) => o.id === item?.id,
            );

            setObjectToEdit(obj as BimWorkOrderObjectSelectionData);

            if (item?.parent) {
                const parent =
                    bimWorkOrderChangeObjectSelectionListData?.bimWorkOrderChangeObjectSelectionList?.items?.find(
                        (o) => o.id === item?.parent?.id,
                    );

                setObjectToAppendTo(parent as BimWorkOrderObjectSelectionData);
                setIsOpenCreateOrEditSubObject(true);
            } else {
                setIsOpenCreateOrEditObject(true);
            }
        },
        [bimWorkOrderChangeObjectSelectionListData?.bimWorkOrderChangeObjectSelectionList?.items],
    );

    const menuItemsGetter = useCallback(
        (treeItem: ITableLikeTreeItem) => {
            const treeItemData =
                bimWorkOrderChangeObjectSelectionListData?.bimWorkOrderChangeObjectSelectionList?.items?.find(
                    (o) => o.id === treeItem.id,
                );

            const isEditDisabled =
                (workOrderStatus === WorkOrderStatusEnum.InProgress &&
                    treeItemData?.status === BimContractObjectStatusEnum.ContractCondition) ||
                treeItemData?.hasActsKC2 ||
                !isWorkOrderChangeStatusOnFormation ||
                treeItemData?.status === BimContractObjectStatusEnum.ContractCondition;
            const isDeleteDisabled =
                !isWorkOrderChangeStatusOnFormation ||
                !!treeItemData?.hasActsKC2 ||
                treeItemData?.status !== BimContractObjectStatusEnum.NewRecord ||
                !!treeItemData?.isInUse;
            const isCreateSubObjDisabled =
                treeItemData?.hasPrices ||
                !isWorkOrderChangeStatusOnFormation ||
                treeItemData?.status === BimContractObjectStatusEnum.ContractCondition;

            return [
                {
                    label: 'Редактировать',
                    action: onOpenEditObject,
                    disabled: isEditDisabled,
                    itemId: 1,
                    groupId: 1,
                },
                {
                    label: 'Создать новый подобъект',
                    itemId: 2,
                    groupId: 1,
                    action: onOpenCreateSubObject,
                    disabled: isCreateSubObjDisabled,
                },
                {
                    label: 'Удалить',
                    action: onDeleteObject,
                    disabled: isDeleteDisabled,
                    itemId: 3,
                    groupId: 2,
                },
            ];
        },
        [
            bimWorkOrderChangeObjectSelectionListData?.bimWorkOrderChangeObjectSelectionList?.items,
            isWorkOrderChangeStatusOnFormation,
            onDeleteObject,
            onOpenCreateSubObject,
            onOpenEditObject,
            workOrderStatus,
        ],
    );

    const onItemCheckChange = useCallback(
        (item: ITableLikeTreeItem, isChecked: boolean) => {
            if (isChecked) {
                bimIncludeContractObjectToWorkOrderMakeRequest(item.id);
            } else {
                bimExcludeContractObjectFromWorkOrderMakeRequest(item.id);
            }
        },
        [bimExcludeContractObjectFromWorkOrderMakeRequest, bimIncludeContractObjectToWorkOrderMakeRequest],
    );

    const onOpenCreateObject = useCallback(() => {
        setOperationToConfirm(BimWorkOrderStructureOperation.CreateObject);

        handleOpenConfirmationDialog();
    }, [handleOpenConfirmationDialog]);

    const onCompleteClick = useCallback(() => {
        navigate(`/projects/bim/${projectId}/orderChange/${workOrderChangeId}`);
    }, [navigate, projectId, workOrderChangeId]);

    const onShowOnlyCheckedChange = useCallback(({ checked }) => {
        setShowOnlyChecked(checked);
    }, []);

    const onLeftFilterRowSelected = useCallback((itemId: number) => {
        setConstructionTypeId(itemId);
    }, []);

    const onCreateObjectClose = useCallback(
        (isSuccess?: boolean) => {
            setIsOpenCreateOrEditObject(false);
            setOperationToConfirm(undefined);
            setObjectToEdit(undefined);

            if (isSuccess && workOrderChangeId && constructionTypeId) {
                bimWorkOrderChangeObjectSelectionListMakeRequest(workOrderChangeId, constructionTypeId);
                updateIncludedCount(1);
            }
        },
        [bimWorkOrderChangeObjectSelectionListMakeRequest, constructionTypeId, updateIncludedCount, workOrderChangeId],
    );

    const onCreateOrEditSubObjectClose = useCallback(
        (isSuccess?: boolean) => {
            if (isSuccess && workOrderChangeId) {
                bimWorkOrderChangeObjectSelectionListMakeRequest(workOrderChangeId, constructionTypeId);
            }

            setIsOpenCreateOrEditSubObject(false);
            setObjectToAppendTo(undefined);
            setObjectToEdit(undefined);
        },
        [bimWorkOrderChangeObjectSelectionListMakeRequest, constructionTypeId, workOrderChangeId],
    );

    useEffect(() => {
        if (workOrderChangeId && constructionTypeId) {
            bimWorkOrderChangeObjectSelectionListMakeRequest(workOrderChangeId, constructionTypeId);
        }
    }, [bimWorkOrderChangeObjectSelectionListMakeRequest, constructionTypeId, workOrderChangeId]);

    useEffect(() => {
        bimWorkOrderSeasonSelectionReset(undefined);
        bimWorkOrderChangeObjectSelectionListReset(undefined);

        return () => {
            bimWorkOrderSeasonSelectionReset(undefined);
            bimWorkOrderChangeObjectSelectionListReset(undefined);
        };
    }, [bimWorkOrderChangeObjectSelectionListReset, bimWorkOrderSeasonSelectionReset]);

    useEffect(() => {
        if (bimWorkOrderChangeObjectSelectionListData?.bimWorkOrderChangeObjectSelectionList?.items?.length) {
            const checkedItems = bimWorkOrderChangeObjectSelectionListData?.bimWorkOrderChangeObjectSelectionList?.items
                .filter((i) => i.isIncluded)
                .map((i) => i.id);

            updateIncludedCount(checkedItems.length, true);
            setCheckedIds(checkedItems);
        }
    }, [
        bimWorkOrderChangeObjectSelectionListData?.bimWorkOrderChangeObjectSelectionList?.items,
        constructionTypeId,
        updateIncludedCount,
    ]);

    useEffect(() => {
        const onResize = () => {
            if (tableRef.current) {
                setWorkOrderStructureStyle({
                    '--others-height':
                        isNumber(tableRef.current?.getBoundingClientRect?.()?.top) &&
                        `${(tableRef.current?.getBoundingClientRect?.()?.top as number) + 91}px`,
                } as React.CSSProperties);
            }
        };

        window.addEventListener('resize', onResize);

        onResize();

        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, []);

    useEffect(() => {
        if (sortedConstructionTypesTabsItems.length) {
            setConstructionTypeId(sortedConstructionTypesTabsItems[0].id);
        }
    }, [sortedConstructionTypesTabsItems]);

    const onCreateReport = () => {
        ExportCSVReport(bimRemoveWorkOrderChangeObjectData?.bimRemoveWorkOrderChangeObject.validation);
    };

    const onCloseModalError = useCallback(() => {
        setIsErrorSubmitted(false);
    }, []);

    return (
        <ProjectPageLayout title={title} breadCrumbsItems={breadCrumbsItems} style={{ height: 'calc(100% - 60px)' }}>
            <Layout className={cn(styles.wrapper_toolbar, cnMixSpace({ mB: 'xl' }))}>
                <Button style={{ marginRight: '10px' }} label={saveButtonTitle} size="s" onClick={onCompleteClick} />
            </Layout>
            <div id="workOrderStructure" style={workOrderStructureStyle}>
                {isLoadingPage ? (
                    <Loader />
                ) : (
                    <div className={styles.bimWorkOrderStructureContainer} ref={tableRef}>
                        <TableLikeTree
                            hierarchyColumnTitle=""
                            items={tableTreeItems}
                            menuItemsGetter={menuItemsGetter}
                            isRowCheckAvailable
                            showExpandAllSwitchInHierarchyHeader
                            leftFilterConfig={leftFilterConfig}
                            topFilterConfig={topFilterConfig}
                            topFilterCheckedIds={selectedSeasonsIds}
                            onItemCheckChange={onItemCheckChange}
                            itemsCheckedIds={checkedIds}
                            areCheckboxesDisabled={areCheckboxesDisabled}
                            areOuterBordersEnabled
                            tableTreeColumnLeftSide={
                                <Switch
                                    label="Показать выбранные"
                                    checked={showOnlyChecked}
                                    size="s"
                                    onChange={onShowOnlyCheckedChange}
                                    style={{ marginRight: 20 }}
                                />
                            }
                            tableTreeColumnRightSide={
                                <Button
                                    label="Создать новый объект"
                                    onClick={onOpenCreateObject}
                                    view="secondary"
                                    size="s"
                                    style={{ marginLeft: 'auto' }}
                                    disabled={!isWorkOrderChangeStatusOnFormation}
                                />
                            }
                            showCheckedOnly={showOnlyChecked}
                            onLeftFilterRowSelected={onLeftFilterRowSelected}
                            leftFilterSelectedId={constructionTypeId}
                            informers={informers}
                            checkConfirmations={checkConfirmations}
                            disabledCheckBoxesItemsIds={disabledCheckBoxesItemsIds}
                            dragOperationDisabledItemsIds={dragOperationDisabledItemsIds}
                            isLeftFilterMenuColumnVisible={false}
                            showCheckedItemsCount
                            isDragOperationDisabled
                            isDragButtonHidden
                            leftFilterCheckedCountCache={includedCount}
                            isOrderChangePage={!!orderChangeId}
                            isHeaderCheckboxDisabled={!!orderChangeId}
                        />
                    </div>
                )}
            </div>
            {isOpenCreateOrEditObject && constructionTypeId && (
                <BimWorkOrderObjectEdit
                    workOrderChangeId={workOrderChangeId}
                    contractId={contractId}
                    constructionTypeId={constructionTypeId}
                    onClose={onCreateObjectClose}
                    seasons={bimWorkOrderContractSeasonListData}
                    objectToEdit={objectToEdit}
                    isNameUnconditionallyEnabled
                />
            )}
            {isOpenCreateOrEditSubObject && (
                <BimWorkOrderSubObjectEdit
                    workOrderChangeId={workOrderChangeId}
                    onClose={onCreateOrEditSubObjectClose}
                    objectToEdit={objectToEdit}
                    parentObjectToAppend={objectToAppendTo}
                    isNameUnconditionallyEnabled
                />
            )}
            <ConfirmationDialog
                textHeader={textHeader}
                textConfirmation={textConfirmation}
                cancelButtonText={cancelButtonText}
                confirmButtonText={confirmButtonText}
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
            <ErrorHandlingModal
                titleText="Удаление объекта"
                isSubmitted={isErrorSubmitted}
                statusRequest={bimRemoveWorkOrderChangeObjectStatus}
                dataRequest={bimRemoveWorkOrderChangeObjectData?.bimRemoveWorkOrderChangeObject}
                errorsRequest={bimRemoveWorkOrderChangeObjectError}
                handleCloseModalError={onCloseModalError}
                handleCreateReport={onCreateReport}
            />
        </ProjectPageLayout>
    );
};

BimWorkOrderChangeStructure.displayName = 'BimWorkOrderChangeStructure';
